import { SyntheticEvent, useMemo } from 'react';
import { When } from 'react-if';
// eslint-disable-next-line no-restricted-imports -- 기존 NiceModal 기반 홈 모달입니다. overlay-kit 전환은 별도 리팩토링에서 처리합니다.
import { useModal } from '@ebay/nice-modal-react';
import cns from 'classnames';
import { shuffle } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useIsomorphicViewportDevice, useViewportDevice } from '@zep/react';
import { Button } from '@zep/ui';
import { splitArray } from '@zep/utils';
import { ReferenceItem } from '../../../types';
import { YoutubeFillIcon } from '../../icons';
import { InquiryBanner } from '../stores';
import S from './ExploreGrid.module.scss';
import { ExploreInquiryModal } from './ExploreInquiryModal';
import { ExploreYoutubeModal } from './ExploreYoutubeModal';
const ExploreGrid = (props: ReferenceGridProps) => {
  const {
    selectedCategory,
    items,
    partners
  } = props;
  const {
    isLaptop,
    isTablet,
    isMobile
  } = useIsomorphicViewportDevice();
  const validAllItems = useMemo(() => {
    const nowDate = new Date().toISOString().substring(0, 10);
    const validItems = items.filter(item => item.startDate && nowDate >= item.startDate && (!item.endDate || nowDate <= item.endDate));
    const {
      filteredData: pinItems,
      remainData: unpinnedData
    } = splitArray(validItems, item => item.isPin);
    const {
      filteredData: premiumItems,
      remainData: freeData
    } = splitArray(unpinnedData, item => item.cardType === 'premium');
    const {
      filteredData: youtubeItems,
      remainData: normalItems
    } = splitArray(freeData, item => item.cardType === 'youtube');
    return [...shuffle(pinItems), ...shuffle(premiumItems), ...shuffle(youtubeItems), ...shuffle(normalItems)];
  }, [items]);
  const filteredItems = useMemo(() => {
    const printableItems = validAllItems.filter(item => !selectedCategory || item.category.includes(selectedCategory));
    const gridSplitItemCount = selectedCategory ? printableItems.length : (isMobile || isTablet ? 2 : isLaptop ? 3 : 4) * 5;
    return [printableItems.slice(0, gridSplitItemCount), printableItems.slice(gridSplitItemCount)];
  }, [validAllItems, selectedCategory, isMobile, isTablet, isLaptop]);
  return <div className={S.explore_items} data-sentry-component="ExploreGrid" data-sentry-source-file="ExploreGrid.tsx">
      <div className={S.reference_grid}>
        {filteredItems[0].map(item => <ExploreCard key={item.title} item={item} selectedCategory={selectedCategory} partnerLogo={partners[item.partnerName]?.logo} />)}
      </div>
      <When condition={filteredItems[1].length > 0} data-sentry-element="When" data-sentry-source-file="ExploreGrid.tsx">
        <InquiryBanner data-sentry-element="InquiryBanner" data-sentry-source-file="ExploreGrid.tsx" />
        <div className={S.reference_grid}>
          {filteredItems[1].map(item => <ExploreCard key={item.title} item={item} selectedCategory={selectedCategory} partnerLogo={partners[item.partnerName]?.logo} />)}
        </div>
      </When>
    </div>;
};
const ExploreCard = (props: {
  item: ReferenceItem;
  selectedCategory: string;
  partnerLogo?: string;
}) => {
  const {
    item,
    selectedCategory,
    partnerLogo
  } = props;
  const {
    t
  } = useTranslation(['home']);
  const referenceYoutubeModal = useModal(ExploreYoutubeModal);
  const referenceInquiryModal = useModal(ExploreInquiryModal);
  const category = selectedCategory || item.category?.[0] || ' ';
  const {
    isMobile
  } = useViewportDevice();
  const cardAction = () => {
    if (item.cardType === 'youtube') {
      void referenceYoutubeModal.show({
        url: item.link,
        title: item.title
      });
    } else {
      window.open(item.link);
    }
    logger.track('home_explore_card_click', {
      name: item.title,
      cardType: item.cardType
    });
  };
  const showInquiryModal = () => {
    void referenceInquiryModal.show({
      spaceName: item.title,
      partnerName: item.partnerName,
      spaceTemplateUrl: item.thumbnail
    });
    logger.track('home_explore_card_partner_inquiry', {
      partnerName: item.partnerName,
      spaceName: item.title
    });
  };
  const imageOnError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    (event.target as HTMLImageElement).style.display = 'none';
  };
  return <div className={S.explore_wrapper} data-sentry-component="ExploreCard" data-sentry-source-file="ExploreGrid.tsx">
      <div className={S.explore_item} onClick={cardAction}>
        <div className={S.item_thumbnail}>
          <img className={S.item_image} src={item.thumbnail} alt={item.title} loading={'lazy'} onError={imageOnError} />
        </div>
        <When condition={item.cardType === 'youtube'} data-sentry-element="When" data-sentry-source-file="ExploreGrid.tsx">
          <div className={S.youtube_icon}>
            <span className={S.youtube_icon_background} />
            <YoutubeFillIcon className={S.youtube_icon_foreground} width={isMobile ? 30 : 36} height={isMobile ? 30 : 36} fill={'#E9381D'} data-sentry-element="YoutubeFillIcon" data-sentry-source-file="ExploreGrid.tsx" />
          </div>
        </When>
        <div className={S.item_category}>{category}</div>
        <div className={cns(S.item_title, {
        [S.premium]: item.cardType === 'premium'
      })}>
          {item.title}
        </div>
      </div>
      <When condition={item.cardType === 'premium'} data-sentry-element="When" data-sentry-source-file="ExploreGrid.tsx">
        <div className={S.explore_premium_wrapper} onClick={showInquiryModal}>
          <div className={S.partner_information}>
            <div className={S.partner_logo}>
              <img src={partnerLogo} alt={`${item.partnerName} logo`} onError={imageOnError} />
            </div>
            <span className={S.partner_title}>
              <span>{item.partnerName}</span>
              <img className={S.premium_partners_icon} src="/images/partners/premium-partners-icon.png" alt="premium-partners" />
            </span>
          </div>
          <div>
            <Button className={S.active_inquiry_button} size={'xxs'} rounded data-sentry-element="Button" data-sentry-source-file="ExploreGrid.tsx">
              <span>{t('home:explore.construction.inquiry')}</span>
            </Button>
            <Button className={S.inactive_inquiry_button} size={'xxs'} variant={'sub'} rounded data-sentry-element="Button" data-sentry-source-file="ExploreGrid.tsx">
              <span>{t('home:explore.construction.inquiry')}</span>
            </Button>
          </div>
        </div>
      </When>
    </div>;
};
export default ExploreGrid;
type ReferenceGridProps = {
  selectedCategory: string;
  partners: Record<string, {
    name: string;
    logo: string;
  }>;
  items: ReferenceItem[];
};